import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export type ConferencePhase = 'PreJoin' | 'Connecting' | 'Connected' | 'Reconnecting' | 'Exiting' | 'Exited'

const VALID_TRANSITIONS: Record<ConferencePhase, ConferencePhase[]> = {
    PreJoin:      ['Connecting'],
    Connecting:   ['Connected', 'Exiting', 'Exited'],
    Connected:    ['Reconnecting', 'Exiting'],
    Reconnecting: ['Connected', 'Exiting', 'Exited'],
    Exiting:      ['Exited'],
    Exited:       ['PreJoin'],
}

export const useConferenceLifecycle = defineStore('conferenceLifecycle', () => {
    const phase = ref<ConferencePhase>('PreJoin')

    const showPreJoin = computed(() => phase.value === 'PreJoin')
    const hasJoined = computed(() => phase.value !== 'PreJoin' && phase.value !== 'Exited')
    const isConnected = computed(() => phase.value === 'Connected')
    const isReconnecting = computed(() => phase.value === 'Reconnecting')

    function transition(to: ConferencePhase, event?: string): boolean {
        const from = phase.value
        if (!VALID_TRANSITIONS[from]?.includes(to)) {
            console.warn(`[Lifecycle] invalid: ${from} → ${to}`, event)
            return false
        }
        console.log(`[Lifecycle] ${from} → ${to}`, event ?? '')
        phase.value = to
        return true
    }

    function join() { transition('Connecting', 'JOIN') }

    function exit() {
        if (phase.value === 'PreJoin' || phase.value === 'Exited' || phase.value === 'Exiting') return
        transition('Exiting', 'EXIT')
    }

    function onWsOpen() {
        if (phase.value === 'Connecting' || phase.value === 'Reconnecting') {
            transition('Connected', 'WS_OPEN')
        }
    }

    function onWsClose() {
        if (phase.value === 'Connected') {
            transition('Reconnecting', 'WS_CLOSE')
        }
    }

    function onCleanupDone() {
        transition('Exited', 'CLEANUP_DONE')
    }

    function reset() {
        transition('PreJoin', 'RESET')
    }

    return { phase, showPreJoin, hasJoined, isConnected, isReconnecting, join, exit, onWsOpen, onWsClose, onCleanupDone, reset }
})
